import { ChangeEvent, FC, ReactElement } from "react";
import cx from "classnames";
import {
  NetworkPortfolioManagementProjectActionStatus,
  ProjectActionFilter,
} from "@app/network-portfolio-management/interfaces";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";

interface BulkActionBarProps {
  /** Active Project Action filter — determines which action (if any) the selection bar offers. */
  filter: ProjectActionFilter;
  selectedCount: number;
  /** True when every project in the filtered cohort is selected (drives the Select All checked state). */
  allSelected: boolean;
  /** True when some — but not all — projects in the filtered cohort are selected (drives the indeterminate state). */
  someSelected: boolean;
  onSelectAllChange: (e: ChangeEvent<HTMLInputElement>) => void;
  isDispatching: boolean;
  onDispatch: (kind: NetworkPortfolioManagementProjectActionStatus) => void;
  /** True while a bulk archive is in flight; disables the action buttons to prevent overlapping ops. */
  isArchiving: boolean;
  /** Opens the Archive confirmation modal for the current selection (only surfaced under the Archive filter). */
  onArchive: () => void;
}

interface ActionConfig {
  kind: NetworkPortfolioManagementProjectActionStatus;
  verb: string;
  /** QESButton variant — Run/Publish are primary (the tone modifier adds blue/green); Archive is destructive red. */
  qesType: "primary" | "primary-danger";
  /** data-testid — Archive keeps a distinct id because it opens a confirmation modal instead of dispatching. */
  testId: string;
  /** Class modifier for the primary actions (blue/green); omitted for Archive, which relies on primary-danger. */
  tone?: "run" | "publish";
}

// The active filter maps 1:1 to the single action the bar surfaces. null surfaces no action. Archive is a config
// entry like Run/Publish, but dispatches through onArchive (a confirmation modal) rather than onDispatch.
const ACTION_BY_FILTER: Partial<Record<NetworkPortfolioManagementProjectActionStatus, ActionConfig>> = {
  [NetworkPortfolioManagementProjectActionStatus.Archive]: { kind: NetworkPortfolioManagementProjectActionStatus.Archive, qesType: "primary-danger", testId: "npm-bulk-archive-button", verb: "Archive" },
  [NetworkPortfolioManagementProjectActionStatus.Publish]: { kind: NetworkPortfolioManagementProjectActionStatus.Publish, qesType: "primary", testId: "npm-bulk-action-button", tone: "publish", verb: "Publish" },
  [NetworkPortfolioManagementProjectActionStatus.Run]: { kind: NetworkPortfolioManagementProjectActionStatus.Run, qesType: "primary", testId: "npm-bulk-action-button", tone: "run", verb: "Run" },
};

/**
 * Selection bar surfaced above the project list for users with a project-action role (Quest User / Client Admin).
 * Select All + the selected count sit on the left; the matching action is right-aligned on the same row — blue Run
 * / green Publish under those filters, or a red Archive under the Archive filter (which has no Run/Publish action).
 * The bar is only rendered once a Project Action filter is active, so the action is always unambiguous. Completion
 * is reported via a toast (see useBulkProjectActions / useBulkArchive), not in this bar.
 */
export const BulkActionBar: FC<BulkActionBarProps> = ({
  filter,
  selectedCount,
  allSelected,
  someSelected,
  onSelectAllChange,
  isDispatching,
  onDispatch,
  isArchiving,
  onArchive,
}) => {
  const action = filter ? ACTION_BY_FILTER[filter] : undefined;
  const hasSelection = selectedCount > 0;
  // Any in-flight bulk op disables every action button so Run/Publish and Archive can't overlap.
  const busy = isDispatching || isArchiving;

  const renderActionSlot = (): ReactElement | null => {
    if (!action) return null;

    // The action button is always shown while a Project Action filter is active; it's disabled until at least one
    // project is selected (and while a bulk op is in flight). Archive opens a confirmation modal via onArchive;
    // Run/Publish dispatch immediately.
    const opensConfirmModal = action.kind === NetworkPortfolioManagementProjectActionStatus.Archive;
    return (
      <QESButton
        className={cx(
          "network-portfolio-management-bulk-action-bar__button",
          action.tone && `network-portfolio-management-bulk-action-bar__button--${action.tone}`,
        )}
        qesType={action.qesType}
        qesTestId={action.testId}
        disabled={busy || !hasSelection}
        onClick={() => (opensConfirmModal ? onArchive() : onDispatch(action.kind))}
      >
        {action.verb} ({selectedCount})
      </QESButton>
    );
  };

  return (
    <div className="network-portfolio-management-bulk-action-bar" data-testid="npm-bulk-action-bar">
      <div className="network-portfolio-management-bulk-action-bar__select">
        <QESCheckbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          label="Select All"
          testId="network-portfolio-management-select-all"
          onChange={onSelectAllChange}
        />
        <span
          className="network-portfolio-management-bulk-action-bar__count"
          data-testid="network-portfolio-management-selected-count"
        >
          {selectedCount.toLocaleString()} selected
        </span>
        <span className="network-portfolio-management-bulk-action-bar__divider" aria-hidden="true" />
      </div>

      <div className="network-portfolio-management-bulk-action-bar__action">
        {renderActionSlot()}
      </div>
    </div>
  );
};
